import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Pencil, Trash2, CheckCircle, XCircle, Clock, Loader2,
  Tag, ShoppingBag, CreditCard, Layers, Package, ChevronDown, ChevronUp,
  AlertTriangle, Shield, BarChart2, FileText, CalendarDays,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/badge';

// ── types ─────────────────────────────────────────────────────────────────────

interface PromotionProduct {
  productId: number;
  productName: string;
  sku: string;
}

interface Tier {
  minQty: number;
  maxQty?: number | null;
  discountValue: number;
}

interface Promotion {
  id: number;
  name: string;
  code: string | null;
  description: string | null;
  type: 'escalonada' | 'volumen' | 'forma_pago' | 'paquete';
  status: 'activa' | 'inactiva' | 'pendiente_autorizacion';
  computedStatus: 'activa' | 'inactiva' | 'pendiente_autorizacion' | 'vencida' | 'pendiente';
  discountType: 'PERCENTAGE' | 'FIXED';
  discountValue: string;
  maxDiscountAmount: string | null;
  stackable: boolean;
  startDate: string | null;
  endDate: string | null;
  minQuantity: string | null;
  tiers: Tier[] | null;
  applicablePaymentMethods: string[] | null;
  requireAllProducts: boolean;
  authorizedByName: string | null;
  authorizedAt: string | null;
  createdByName: string | null;
  createdAt: string;
  products: PromotionProduct[];
}

interface Product {
  id: number;
  sku: string;
  name: string;
  isActive: boolean;
}

// ── API ───────────────────────────────────────────────────────────────────────

async function fetchPromotions(): Promise<Promotion[]> {
  const r = await fetch('/api/promotions', { credentials: 'include' });
  if (!r.ok) throw new Error('Error al cargar promociones');
  return r.json();
}

async function fetchProducts(): Promise<Product[]> {
  const r = await fetch('/api/sales/products', { credentials: 'include' });
  if (!r.ok) throw new Error('Error al cargar productos');
  return r.json();
}

async function apiCreate(data: any) {
  const r = await fetch('/api/promotions', {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Error al crear'); }
  return r.json();
}

async function apiUpdate(id: number, data: any) {
  const r = await fetch(`/api/promotions/${id}`, {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Error al actualizar'); }
  return r.json();
}

async function apiChangeStatus(id: number, status: string) {
  const r = await fetch(`/api/promotions/${id}/status`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Error al cambiar estado'); }
  return r.json();
}

async function apiAuthorize(id: number) {
  const r = await fetch(`/api/promotions/${id}/authorize`, {
    method: 'POST', credentials: 'include',
  });
  if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Error al autorizar'); }
  return r.json();
}

async function apiDelete(id: number) {
  const r = await fetch(`/api/promotions/${id}`, {
    method: 'DELETE', credentials: 'include',
  });
  if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Error al eliminar'); }
  return r.json();
}

interface PromotionUsage {
  invoiceCount: number;
  totalDiscounted: number;
  firstApplied: string | null;
  lastApplied: string | null;
  recentInvoices: {
    invoiceId: number;
    folio: string | null;
    issueDate: string | null;
    alumnoName: string | null;
    discountOnInvoice: number;
  }[];
}

async function fetchPromotionUsage(id: number): Promise<PromotionUsage> {
  const r = await fetch(`/api/promotions/${id}/usage`, { credentials: 'include' });
  if (!r.ok) throw new Error('Error al cargar uso de la promoción');
  return r.json();
}

// ── constants ─────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  escalonada: 'Escalonada',
  volumen: 'Volumen',
  forma_pago: 'Forma de Pago',
  paquete: 'Paquete',
};

const TYPE_ICONS: Record<string, any> = {
  escalonada: Layers,
  volumen: ShoppingBag,
  forma_pago: CreditCard,
  paquete: Package,
};

const TYPE_DESC: Record<string, string> = {
  escalonada: 'Descuento por rangos de cantidad (ej: 1-5 uds → 5%, 6+ uds → 10%)',
  volumen: 'Descuento fijo cuando la cantidad mínima es alcanzada',
  forma_pago: 'Descuento por usar una forma de pago específica (ej: transferencia)',
  paquete: 'Descuento cuando se compran todos los productos del paquete',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  activa: { label: 'Activa', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  inactiva: { label: 'Inactiva', color: 'bg-gray-100 text-gray-700', icon: XCircle },
  pendiente_autorizacion: { label: 'Pend. Autorización', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  vencida: { label: 'Vencida', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  pendiente: { label: 'Pendiente inicio', color: 'bg-blue-100 text-blue-700', icon: Clock },
};

const PAYMENT_METHODS = [
  { value: 'TRANSFER', label: 'Transferencia' },
  { value: 'CASH', label: 'Efectivo' },
  { value: 'CHECK', label: 'Cheque' },
  { value: 'CARD', label: 'Tarjeta' },
  { value: 'OTHER', label: 'Otro' },
];

const EMPTY_FORM = {
  name: '',
  code: '',
  description: '',
  type: 'volumen' as 'escalonada' | 'volumen' | 'forma_pago' | 'paquete',
  discountType: 'PERCENTAGE' as 'PERCENTAGE' | 'FIXED',
  discountValue: '',
  maxDiscountAmount: '',
  stackable: false,
  startDate: '',
  endDate: '',
  minQuantity: '',
  tiers: [] as Tier[],
  applicablePaymentMethods: [] as string[],
  requireAllProducts: true,
  productIds: [] as number[],
};

// ── form component ────────────────────────────────────────────────────────────

function PromotionForm({
  initial,
  products,
  onSave,
  onCancel,
  saving,
}: {
  initial: typeof EMPTY_FORM;
  products: Product[];
  onSave: (data: typeof EMPTY_FORM) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState(initial);
  const [productSearch, setProductSearch] = useState('');

  const set = (key: keyof typeof EMPTY_FORM, val: any) =>
    setForm(prev => ({ ...prev, [key]: val }));

  const addTier = () =>
    setForm(prev => ({
      ...prev,
      tiers: [...prev.tiers, { minQty: 0, maxQty: null, discountValue: 0 }],
    }));

  const updateTier = (i: number, key: keyof Tier, val: any) =>
    setForm(prev => ({
      ...prev,
      tiers: prev.tiers.map((t, idx) => idx === i ? { ...t, [key]: val } : t),
    }));

  const removeTier = (i: number) =>
    setForm(prev => ({ ...prev, tiers: prev.tiers.filter((_, idx) => idx !== i) }));

  const toggleMethod = (m: string) =>
    setForm(prev => ({
      ...prev,
      applicablePaymentMethods: prev.applicablePaymentMethods.includes(m)
        ? prev.applicablePaymentMethods.filter(x => x !== m)
        : [...prev.applicablePaymentMethods, m],
    }));

  const toggleProduct = (pid: number) =>
    setForm(prev => ({
      ...prev,
      productIds: prev.productIds.includes(pid)
        ? prev.productIds.filter(x => x !== pid)
        : [...prev.productIds, pid],
    }));

  const filteredProds = useMemo(() => {
    const s = productSearch.toLowerCase();
    return products.filter(p =>
      p.isActive && (p.name.toLowerCase().includes(s) || p.sku.toLowerCase().includes(s))
    );
  }, [products, productSearch]);

  const needsProducts = form.type === 'escalonada' || form.type === 'volumen' || form.type === 'paquete';
  const needsPaymentMethods = form.type === 'forma_pago';
  const needsTiers = form.type === 'escalonada';
  const needsMinQty = form.type === 'volumen';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Nombre *</Label>
          <Input value={form.name} onChange={e => set('name', e.target.value)} required />
        </div>
        <div className="space-y-1">
          <Label>Código / Clave</Label>
          <Input value={form.code} onChange={e => set('code', e.target.value)} placeholder="PROMO2024" />
        </div>
      </div>

      <div className="space-y-1">
        <Label>Tipo de Promoción *</Label>
        <Select value={form.type} onValueChange={v => set('type', v)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(TYPE_LABELS).map(([v, l]) => (
              <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 mt-1">{TYPE_DESC[form.type]}</p>
      </div>

      <div className="space-y-1">
        <Label>Descripción</Label>
        <Textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label>Tipo de Descuento</Label>
          <Select value={form.discountType} onValueChange={v => set('discountType', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PERCENTAGE">Porcentaje (%)</SelectItem>
              <SelectItem value="FIXED">Monto Fijo ($)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Valor {form.discountType === 'PERCENTAGE' ? '(%)' : '($)'} *</Label>
          <Input
            type="number" step="0.01" min="0"
            value={form.discountValue}
            onChange={e => set('discountValue', e.target.value)}
            required
          />
        </div>
        <div className="space-y-1">
          <Label>Máx. Descuento ($)</Label>
          <Input
            type="number" step="0.01" min="0"
            value={form.maxDiscountAmount}
            onChange={e => set('maxDiscountAmount', e.target.value)}
            placeholder="Sin límite"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Fecha Inicio</Label>
          <Input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Fecha Fin</Label>
          <Input type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
        </div>
      </div>

      {needsMinQty && (
        <div className="space-y-1">
          <Label>Cantidad mínima para activar</Label>
          <Input
            type="number" step="0.01" min="0"
            value={form.minQuantity}
            onChange={e => set('minQuantity', e.target.value)}
            placeholder="Ej: 5"
          />
        </div>
      )}

      {needsTiers && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Rangos de Cantidad</Label>
            <Button type="button" variant="outline" size="sm" onClick={addTier}>
              <Plus className="w-3 h-3 mr-1" /> Agregar rango
            </Button>
          </div>
          {form.tiers.map((tier, i) => (
            <div key={i} className="grid grid-cols-4 gap-2 items-end">
              <div className="space-y-1">
                <Label className="text-xs">Desde (uds)</Label>
                <Input
                  type="number" min="0" step="1"
                  value={tier.minQty}
                  onChange={e => updateTier(i, 'minQty', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hasta (uds)</Label>
                <Input
                  type="number" min="0" step="1"
                  value={tier.maxQty ?? ''}
                  placeholder="Sin límite"
                  onChange={e => updateTier(i, 'maxQty', e.target.value ? parseFloat(e.target.value) : null)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Descuento</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={tier.discountValue}
                  onChange={e => updateTier(i, 'discountValue', parseFloat(e.target.value) || 0)}
                />
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => removeTier(i)}>
                <Trash2 className="w-4 h-4 text-red-400" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {needsPaymentMethods && (
        <div className="space-y-2">
          <Label>Formas de Pago Aplicables *</Label>
          <div className="flex flex-wrap gap-2">
            {PAYMENT_METHODS.map(m => (
              <button
                key={m.value}
                type="button"
                onClick={() => toggleMethod(m.value)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  form.applicablePaymentMethods.includes(m.value)
                    ? 'bg-violet-100 border-violet-400 text-violet-800'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {needsProducts && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Productos Aplicables</Label>
            {form.type === 'paquete' && (
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.requireAllProducts}
                  onChange={e => set('requireAllProducts', e.target.checked)}
                />
                Requiere todos los productos
              </label>
            )}
          </div>
          <Input
            placeholder="Buscar producto..."
            value={productSearch}
            onChange={e => setProductSearch(e.target.value)}
          />
          <div className="max-h-36 overflow-y-auto border rounded-lg divide-y">
            {filteredProds.slice(0, 50).map(p => (
              <label key={p.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.productIds.includes(p.id)}
                  onChange={() => toggleProduct(p.id)}
                />
                <span className="font-mono text-xs text-gray-500">{p.sku}</span>
                <span className="text-sm">{p.name}</span>
              </label>
            ))}
            {filteredProds.length === 0 && (
              <div className="px-3 py-3 text-sm text-gray-400 text-center">Sin resultados</div>
            )}
          </div>
          {form.productIds.length > 0 && (
            <p className="text-xs text-violet-600">{form.productIds.length} producto(s) seleccionado(s)</p>
          )}
        </div>
      )}

      <label className="flex items-center gap-2 cursor-pointer text-sm">
        <input
          type="checkbox"
          checked={form.stackable}
          onChange={e => set('stackable', e.target.checked)}
        />
        <span>¿Acumulable con descuento del producto?</span>
      </label>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button type="submit" disabled={saving} className="bg-violet-600 hover:bg-violet-700">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Guardar
        </Button>
      </DialogFooter>
    </form>
  );
}

// ── usage panel component ─────────────────────────────────────────────────────

function PromotionUsagePanel({
  promotionId,
  fmtCurrency,
}: {
  promotionId: number;
  fmtCurrency: (n: string | number) => string;
}) {
  const { data, isLoading, isError } = useQuery<PromotionUsage>({
    queryKey: ['promotion-usage', promotionId],
    queryFn: () => fetchPromotionUsage(promotionId),
  });

  const fmtDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="px-4 py-6 text-center text-sm text-red-500">
        No se pudo cargar el reporte de uso.
      </div>
    );
  }

  return (
    <div className="px-4 py-3 space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 mb-1">Facturas con descuento</p>
          <p className="text-2xl font-bold text-violet-700">{data.invoiceCount}</p>
        </div>
        <div className="bg-white border rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 mb-1">Total descontado</p>
          <p className="text-2xl font-bold text-emerald-600">${fmtCurrency(data.totalDiscounted)}</p>
        </div>
        <div className="bg-white border rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 mb-1 flex items-center justify-center gap-1">
            <CalendarDays className="w-3 h-3" /> Primera aplicación
          </p>
          <p className="text-sm font-semibold text-gray-700">{fmtDate(data.firstApplied)}</p>
        </div>
        <div className="bg-white border rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 mb-1 flex items-center justify-center gap-1">
            <CalendarDays className="w-3 h-3" /> Última aplicación
          </p>
          <p className="text-sm font-semibold text-gray-700">{fmtDate(data.lastApplied)}</p>
        </div>
      </div>

      {/* Recent invoices */}
      {data.recentInvoices.length > 0 ? (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Últimas facturas con esta promoción</p>
          <div className="bg-white border rounded-lg divide-y overflow-hidden">
            {data.recentInvoices.map(inv => (
              <div key={inv.invoiceId} className="flex items-center justify-between px-3 py-2 text-xs hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-gray-500">{inv.folio ?? `#${inv.invoiceId}`}</span>
                  {inv.alumnoName && (
                    <span className="text-gray-700 truncate max-w-[200px]">{inv.alumnoName}</span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-right shrink-0">
                  <span className="text-gray-400">{fmtDate(inv.issueDate)}</span>
                  <span className="font-semibold text-emerald-600">-${fmtCurrency(inv.discountOnInvoice)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-4 text-sm text-gray-400">
          Esta promoción aún no se ha aplicado en ninguna factura.
        </div>
      )}
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function PromotionsList() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canAuthorize = user?.permissions?.includes('promotions:authorize');

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedTab, setExpandedTab] = useState<'details' | 'uso'>('details');

  const { data: promotions = [], isLoading } = useQuery({
    queryKey: ['promotions'],
    queryFn: fetchPromotions,
  });

  const { data: products = [] } = useQuery({
    queryKey: ['sales-products'],
    queryFn: fetchProducts,
  });

  const createMutation = useMutation({
    mutationFn: apiCreate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotions-active'] });
      toast({ title: 'Promoción creada', description: 'Pendiente de autorización' });
      setShowForm(false);
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiUpdate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotions-active'] });
      toast({ title: 'Promoción actualizada' });
      setEditingPromo(null);
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => apiChangeStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotions-active'] });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const authorizeMutation = useMutation({
    mutationFn: apiAuthorize,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotions-active'] });
      toast({ title: 'Promoción autorizada', description: 'La promoción está activa' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: apiDelete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotions-active'] });
      toast({ title: 'Promoción desactivada' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const filtered = useMemo(() => {
    return promotions.filter(p => {
      if (filterType !== 'all' && p.type !== filterType) return false;
      if (filterStatus !== 'all' && p.computedStatus !== filterStatus) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!p.name.toLowerCase().includes(s) && !(p.code?.toLowerCase() || '').includes(s)) return false;
      }
      return true;
    });
  }, [promotions, filterType, filterStatus, search]);

  const handleCreate = (formData: typeof EMPTY_FORM) => {
    createMutation.mutate({
      ...formData,
      discountValue: parseFloat(formData.discountValue) || 0,
      maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null,
      minQuantity: formData.minQuantity ? parseFloat(formData.minQuantity) : null,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      code: formData.code || null,
      description: formData.description || null,
    });
  };

  const handleUpdate = (formData: typeof EMPTY_FORM) => {
    if (!editingPromo) return;
    updateMutation.mutate({
      id: editingPromo.id,
      data: {
        ...formData,
        discountValue: parseFloat(formData.discountValue) || 0,
        maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null,
        minQuantity: formData.minQuantity ? parseFloat(formData.minQuantity) : null,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        code: formData.code || null,
        description: formData.description || null,
      },
    });
  };

  const openEdit = (p: Promotion) => {
    setEditingPromo(p);
  };

  const editInitial = editingPromo
    ? {
        name: editingPromo.name,
        code: editingPromo.code || '',
        description: editingPromo.description || '',
        type: editingPromo.type,
        discountType: editingPromo.discountType,
        discountValue: editingPromo.discountValue,
        maxDiscountAmount: editingPromo.maxDiscountAmount || '',
        stackable: editingPromo.stackable,
        startDate: editingPromo.startDate || '',
        endDate: editingPromo.endDate || '',
        minQuantity: editingPromo.minQuantity || '',
        tiers: (editingPromo.tiers || []) as Tier[],
        applicablePaymentMethods: editingPromo.applicablePaymentMethods || [],
        requireAllProducts: editingPromo.requireAllProducts,
        productIds: editingPromo.products.map(pp => pp.productId),
      }
    : EMPTY_FORM;

  const fmt = (n: string | number) =>
    parseFloat(String(n)).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Tag className="w-6 h-6 text-violet-600" />
            Módulo de Promociones
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestiona descuentos por volumen, escalonados, forma de pago y paquetes
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-violet-600 hover:bg-violet-700">
          <Plus className="w-4 h-4 mr-2" /> Nueva Promoción
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white border rounded-xl p-4">
        <Input
          placeholder="Buscar por nombre o código..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-64"
        />
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {Object.entries(TYPE_LABELS).map(([v, l]) => (
              <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([v, c]) => (
              <SelectItem key={v} value={v}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Tag className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No hay promociones{search || filterType !== 'all' || filterStatus !== 'all' ? ' con esos filtros' : ' registradas'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(p => {
            const TypeIcon = TYPE_ICONS[p.type] || Tag;
            const sc = STATUS_CONFIG[p.computedStatus] || STATUS_CONFIG.inactiva;
            const StatusIcon = sc.icon;
            const isExpanded = expandedId === p.id;

            return (
              <div key={p.id} className="bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 p-4">
                  <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                    <TypeIcon className="w-5 h-5 text-violet-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 truncate">{p.name}</span>
                      {p.code && (
                        <span className="font-mono text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{p.code}</span>
                      )}
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${sc.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {sc.label}
                      </span>
                      <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full border">{TYPE_LABELS[p.type]}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 flex-wrap">
                      <span>
                        {p.discountType === 'PERCENTAGE'
                          ? `${fmt(p.discountValue)}%`
                          : `$${fmt(p.discountValue)}`} de descuento
                        {p.maxDiscountAmount ? ` (máx $${fmt(p.maxDiscountAmount)})` : ''}
                      </span>
                      {p.stackable && <span className="text-violet-600 text-xs">• Acumulable</span>}
                      {p.startDate && <span>Desde {p.startDate}</span>}
                      {p.endDate && <span>Hasta {p.endDate}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {p.computedStatus === 'pendiente_autorizacion' && canAuthorize && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-700 border-green-300 hover:bg-green-50"
                        onClick={() => { if (confirm('¿Autorizar esta promoción?')) authorizeMutation.mutate(p.id); }}
                      >
                        <Shield className="w-3 h-3 mr-1" /> Autorizar
                      </Button>
                    )}
                    {p.status === 'activa' && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => statusMutation.mutate({ id: p.id, status: 'inactiva' })}
                        title="Desactivar"
                      >
                        <XCircle className="w-4 h-4 text-gray-400" />
                      </Button>
                    )}
                    {p.status === 'inactiva' && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => statusMutation.mutate({ id: p.id, status: 'pendiente_autorizacion' })}
                        title="Reactivar (irá a autorización)"
                      >
                        <CheckCircle className="w-4 h-4 text-gray-400" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>
                      <Pencil className="w-4 h-4 text-gray-400" />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => { if (confirm('¿Desactivar esta promoción?')) deleteMutation.mutate(p.id); }}
                    >
                      <Trash2 className="w-4 h-4 text-gray-400" />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => {
                        if (isExpanded) {
                          setExpandedId(null);
                        } else {
                          setExpandedId(p.id);
                          setExpandedTab('details');
                        }
                      }}
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t bg-gray-50 text-sm">
                    {/* Tabs */}
                    <div className="flex border-b bg-white px-4">
                      <button
                        className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                          expandedTab === 'details'
                            ? 'border-violet-600 text-violet-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => setExpandedTab('details')}
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Detalles
                      </button>
                      <button
                        className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                          expandedTab === 'uso'
                            ? 'border-violet-600 text-violet-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => setExpandedTab('uso')}
                      >
                        <BarChart2 className="w-3.5 h-3.5" />
                        Uso
                      </button>
                    </div>

                    {expandedTab === 'details' && (
                      <div className="px-4 py-3 space-y-2">
                        {p.description && <p className="text-gray-600">{p.description}</p>}

                        {p.type === 'escalonada' && p.tiers && p.tiers.length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">Rangos:</span>
                            <div className="mt-1 flex flex-wrap gap-2">
                              {p.tiers.map((t, i) => (
                                <span key={i} className="bg-white border rounded px-2 py-1 text-xs">
                                  {t.minQty}{t.maxQty ? `–${t.maxQty}` : '+'} uds → {fmt(t.discountValue)}{p.discountType === 'PERCENTAGE' ? '%' : '$'}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {p.type === 'volumen' && p.minQuantity && (
                          <p><span className="font-medium">Cantidad mínima:</span> {fmt(p.minQuantity)} unidades</p>
                        )}

                        {p.type === 'forma_pago' && p.applicablePaymentMethods && (
                          <div>
                            <span className="font-medium text-gray-700">Formas de pago:</span>{' '}
                            {p.applicablePaymentMethods.map(m => PAYMENT_METHODS.find(x => x.value === m)?.label || m).join(', ')}
                          </div>
                        )}

                        {p.products.length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">Productos ({p.products.length}):</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {p.products.map(pp => (
                                <span key={pp.productId} className="bg-white border text-xs px-2 py-0.5 rounded">
                                  {pp.sku} – {pp.productName}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-6 text-xs text-gray-400 pt-1">
                          {p.createdByName && <span>Creado por: {p.createdByName}</span>}
                          {p.authorizedByName && <span>Autorizado por: {p.authorizedByName}</span>}
                        </div>
                      </div>
                    )}

                    {expandedTab === 'uso' && (
                      <PromotionUsagePanel promotionId={p.id} fmtCurrency={fmt} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-[620px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-violet-600" />
              Nueva Promoción
            </DialogTitle>
          </DialogHeader>
          <PromotionForm
            initial={EMPTY_FORM}
            products={products}
            onSave={handleCreate}
            onCancel={() => setShowForm(false)}
            saving={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editingPromo} onOpenChange={open => { if (!open) setEditingPromo(null); }}>
        <DialogContent className="sm:max-w-[620px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-violet-600" />
              Editar Promoción
            </DialogTitle>
          </DialogHeader>
          {editingPromo && (
            <PromotionForm
              initial={editInitial}
              products={products}
              onSave={handleUpdate}
              onCancel={() => setEditingPromo(null)}
              saving={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
